/**
 * CoWork bridge — headless subscriber that keeps the CoWork store in sync with
 * the desktop shell (hybrid CoWork, D3). Mounted ONCE at the app root so the
 * approval-request and change subscriptions are stable: a chat re-render can
 * never drop a pending approval (the queue lives in the store, fed from here).
 *
 * Desktop-only; renders nothing.
 *
 * @module components/cowork/cowork-bridge
 */

import { useEffect } from "react";

import { getDesktop, type WorkspaceChange } from "@/lib/desktop";
import { useCoworkStore } from "@/store/cowork-store";

/**
 * Rolling cap on the in-memory activity buffer. Reads are frequent, so an
 * unbounded buffer would grow memory + re-render cost over a long session. We
 * keep the most-recent N events (the Activity feed only shows the last ~40).
 */
const MAX_ACTIVITY = 200;

const changeKey = (c: WorkspaceChange): string => `${c.path}|${c.at}|${c.kind}`;

/** Keep only the last MAX_ACTIVITY events (drop the oldest). */
const cap = (list: WorkspaceChange[]): WorkspaceChange[] =>
  list.length > MAX_ACTIVITY ? list.slice(list.length - MAX_ACTIVITY) : list;

export function CoworkBridge() {
  useEffect(() => {
    const bridge = getDesktop();
    if (!bridge) return;

    const offApproval = bridge.cowork.onApprovalRequest((req) =>
      useCoworkStore.getState().enqueueApproval(req),
    );
    const offChanges = bridge.cowork.onChanges((event) => {
      // Apply the delta to the store (per-event, not a full-array replace).
      const store = useCoworkStore.getState();
      const cur = store.changes;
      if (event.type === "reset") {
        store.setChanges(cap(event.changes));
        return;
      }
      if (event.type === "remove") {
        // Drop the mutation ledger entry for this path (never a read event).
        const next = cur.filter((c) => !(c.path === event.path && c.kind !== "read"));
        if (next.length !== cur.length) store.setChanges(next);
        return;
      }
      // add — dedup by identity so a re-hydration/replay can't double-count.
      const key = changeKey(event.change);
      if (cur.some((c) => changeKey(c) === key)) return;
      store.setChanges(cap([...cur, event.change]));
    });
    // Hydrate the current change set on mount so a change that fired before this
    // subscriber attached (e.g. after a reload) isn't lost until the next one.
    void bridge.cowork.getChanges().then((changes) => {
      if (changes?.length) useCoworkStore.getState().setChanges(cap(changes));
    }).catch(() => {});
    const offStatus = bridge.cowork.onStatus((status) =>
      useCoworkStore.getState().setConnected(status.connected),
    );

    return () => {
      offApproval();
      offChanges();
      offStatus();
    };
  }, []);

  return null;
}
